/**
 * Pending Voucher Session Storage Utilities
 *
 * Manages sessionStorage state for voucher claim banners on marketing sites.
 * When a user lands on a /claim/:code page, voucher data is saved so that
 * a persistent banner can remind them to claim on other pages.
 */

const STORAGE_KEY = 'pendingVoucher';
const DISMISSED_KEY_PREFIX = 'pendingVoucherDismissed:';

export interface PendingVoucherData {
  shareCode: string;
  merchantName: string;
  displayAmount: string;
  claimUrl: string;
}

export function savePendingVoucher(data: PendingVoucherData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors (private browsing, quota exceeded)
  }
}

export function getPendingVoucher(): PendingVoucherData | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.shareCode !== 'string' ||
      typeof parsed?.merchantName !== 'string' ||
      typeof parsed?.displayAmount !== 'string' ||
      typeof parsed?.claimUrl !== 'string'
    ) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as PendingVoucherData;
  } catch {
    return null;
  }
}

export function clearPendingVoucher(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}

export function isPendingVoucherDismissed(shareCode: string): boolean {
  try {
    return sessionStorage.getItem(`${DISMISSED_KEY_PREFIX}${shareCode}`) === '1';
  } catch {
    return false;
  }
}

export function dismissPendingVoucher(shareCode: string): void {
  try {
    sessionStorage.setItem(`${DISMISSED_KEY_PREFIX}${shareCode}`, '1');
  } catch {
    // Ignore
  }
}
